import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideMapPin } from '@ng-icons/lucide';

export type SvzChipAvatarKind = 'user' | 'area' | 'swatch';

/**
 * Prototype droplist marker (`.sd-av`): a 24px round badge with the person's initials (or picture when the option
 * carries one), the square tinted pin used for areas / locations (`.sd-av-sq`), or the 16px colour swatch of the
 * Colour filter (`.clr-sw`, striped when the option means "no colour"). Shared by the option rows and the trigger
 * chips of `svz-chip` / `svz-chip-grouped`.
 */
@Component({
  selector: 'svz-chip-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon],
  viewProviders: [provideIcons({ lucideMapPin })],
  host: { class: 'inline-flex shrink-0' },
  // Inside a `z-select-item` the row variant forces `size-4` on every svg (`[&_svg:not([class*=size-])]`, important);
  // the pin keeps the prototype 13px through a more specific selector.
  styles: [":host ng-icon[name='lucideMapPin'] ::ng-deep svg { width: 13px !important; height: 13px !important; }"],
  template: `
    @if (kind() === 'swatch') {
      <span
        class="inline-block h-4 w-4 shrink-0 rounded-[5px] border border-[rgba(13,18,47,0.12)]"
        [style.background]="color() || 'repeating-linear-gradient(45deg, #fff 0 4px, #E6E8EE 4px 8px)'"
        aria-hidden="true"
      ></span>
    } @else if (kind() === 'area') {
      <span
        class="flex h-6 w-6 items-center justify-center rounded-[6px] bg-primary/[0.08] text-primary"
        aria-hidden="true"
      >
        <ng-icon
          name="lucideMapPin"
          size="13px"
        />
      </span>
    } @else if (picture()) {
      <span class="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-[#e6e6ea]">
        <img
          [src]="picture()"
          [alt]="name()"
          class="h-full w-full object-cover"
        />
      </span>
    } @else {
      <span
        class="flex h-6 w-6 items-center justify-center rounded-full bg-[#e6e6ea] text-[10px] font-semibold text-[#444444]"
        aria-hidden="true"
      >
        {{ initials() }}
      </span>
    }
  `,
})
export class SvzChipAvatarComponent {
  readonly kind = input<SvzChipAvatarKind>('user');
  readonly name = input<string>('');
  readonly picture = input<string | null | undefined>(null);
  /** `swatch` only: the colour to fill the square with; empty means the striped "no colour" swatch. */
  readonly color = input<string | null | undefined>(null);

  /** Prototype `ini2`: first letter of the first two words. */
  protected readonly initials = computed(() =>
    this.name()
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase()
  );
}
